import React from 'react'
import { Formik, FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch, select } from '@/store'
import { createModal, useModalize, AdditionalModalProps } from '@/components/modalize/mui'
import { breakpoints } from '@/styles'
import LandlordProfileForm from './form'
import { FormValues } from './types'

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 24px 24px;
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 685px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: unset;
  }
`

const getCurrentYear = () => new Date().getFullYear()

type Props = AdditionalModalProps

const EditLandlordProfileModal: React.FC<Props> = ({ ModalTitle, ModalFooter }) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const data: FormValues = useSelector(select.profile.editLandlordProfile)
  const dispatch = useDispatch<Dispatch>()

  const initialValues: FormValues = { ...data, phone: data.phone_number }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(
        /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .required(),
    last_name: Yup.string()
      .matches(
        /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .required(),
    preferred_name: Yup.string().matches(
      /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
      t('b.profile.name.special_characters.error', 'Cannot include special characters')
    ),
    username: Yup.string().email().required(),
    phone: Yup.string().phone().required(),
    company_email: Yup.string().email(),
    company_phone_number: Yup.string().phone(),
    company_unit_number: Yup.string(),
    company_address: Yup.string(),
    company_name: Yup.string(),
    company_city: Yup.string(),
    company_state: Yup.string(),
    company_zip: Yup.string(),
    work_history_year_started: Yup.number().positive().min(1000).max(getCurrentYear())
  })

  const onSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const response = await dispatch.profile.save(values)
    setSubmitting(false)
    if (response.ok) {
      close()
    }
  }

  return (
    <ContentWrapper>
      <ContentInner>
        <ModalTitle>{t('l.profile.edit.edit_profile.title', 'Edit Profile')}</ModalTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, values, isSubmitting, setFieldValue, errors }) => (
            <LandlordProfileForm
              data={data}
              values={values}
              errors={errors}
              handleSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              setFieldValue={setFieldValue}
              close={close}
              ModalFooter={ModalFooter}
            />
          )}
        </Formik>
      </ContentInner>
    </ContentWrapper>
  )
}

export const showEditLandlordProfileModal = createModal(EditLandlordProfileModal, {
  width: '100%',
  maxWidth: '636px',
  sheet: true
})
